import { CdkDragMove } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { AutoFocusDirective } from '../../../actions/@base/directives/auto-focus.directive';
import { UtilitiesService } from '../../../actions/@base/services';
import { CSS_SIZE_UNIT_PX } from "../../../../external/models/css-consts";
import { filter, map, skip } from "rxjs/operators";
import { AbstractDrawerTextComponent, FontSize } from "../drawer-text/abstract-drawer-text.component";
import { DrawerTextUtil } from "../../../utils/drawer-text.util";

enum EditionMode {
  NONE = 'none',
  MOVE_ENABLED = 'moveEnabled',
  WRITE_ENABLED = 'writeEnabled'
}

@Component({
  selector: 'txtb-drawer-text-mobile',
  templateUrl: './drawer-text-mobile.component.html',
  styleUrls: ['./drawer-text-mobile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DrawerTextMobileComponent extends AbstractDrawerTextComponent implements OnDestroy, AfterViewInit {
  @ViewChild(AutoFocusDirective) textAreaElementFocus!: AutoFocusDirective;
  @ViewChild('textContentElement') textContent!: ElementRef<HTMLElement>;
  @ViewChild('dragContainer') dragContainer!: ElementRef;
  @ViewChild('innerWrapper') innerWrapper!: ElementRef<HTMLDivElement>;
  @HostBinding('class.is-locked-center') isLockedCenter = false;

  readonly EditionMode = EditionMode;
  isChanged = false;
  isConnected = true;
  private readonly subscription = new Subscription();
  private readonly saving$ubject = new Subject<void>();
  private readonly editionMode$subject = new BehaviorSubject<EditionMode>(EditionMode.NONE);
  readonly editionMode$ = this.editionMode$subject.asObservable();
  inputText = '';

  private readonly isActiveSubscription = this.editionMode$
    .pipe(
      map((mode) => EditionMode.NONE !== mode),
      distinctUntilChanged()
    ).subscribe((active) => {
      this.isBoxActive = active;
      this.isActive.emit(active);
    });

  private readonly isWritingModeSubscription = this.editionMode$.pipe(map((mode) => EditionMode.WRITE_ENABLED === mode))
    .subscribe((writingMode) => this.isLockedCenter = writingMode);

  constructor(
    public elementRef: ElementRef,
    protected changeDetector: ChangeDetectorRef,
    protected utilitiesService: UtilitiesService,
  ) {
    super(elementRef, changeDetector, utilitiesService);
  }

  get contentIsValid(): boolean {
    return this.content.length <= this.maxLengthText && this.content.length > 0;
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.inputText = this.content;
    this.transformBoxContentPosition();
    this.elementRef.nativeElement.style.fontSize = `${ this.fontSize }${ CSS_SIZE_UNIT_PX }`;

    this.subscription.add(this.isOnline$.subscribe((online) => (this.isConnected = online)));

    if (!this.isConnected) {
      this.content.length === 0 && this.removeNote.emit(this.noteId);
      return;
    }

    this.subscription.add(
      this.currentClickedTarget$.pipe(skip(1)).subscribe((target) => {
        if (
          !this.elementRef.nativeElement.contains(target) &&
          this.inputText.length === 0
        ) {
          this.removeNote.emit(this.noteId);
        }
      })
    );

    this.subscription.add(
      this.saving$ubject.pipe(filter(() => !!this.noteId && this.isChanged)).subscribe(() => {
        if (!this.textColor) {
          return;
        }
        this.emitNoteChanges();
        this.isChanged = false;
      })
    );
    if (this.isBoxActive) {
      this.editionMode$subject.next(EditionMode.WRITE_ENABLED);
    }
  }

  onDragStart(): void {
    this.isDragging = true;
    this.elementRef.nativeElement.style.left = this.x;
    this.elementRef.nativeElement.style.top = this.y;
  }

  onDragEnd(): void {
    this.isDragging = false;
    this.isConnected && this.contentIsValid && this.saving$ubject.next();
  }

  onDragMove(event: CdkDragMove): void {
    this.isChanged = true;

    const dragContainerRect = this.dragContainer?.nativeElement.getBoundingClientRect();
    const parentElementRect = this.wrapper.getBoundingClientRect();
    const position = DrawerTextUtil.calculatePosition(event, dragContainerRect, parentElementRect);
    const xPos = this.utilitiesService.percentage(position.x, parentElementRect.width);
    const yPos = this.utilitiesService.percentage(position.y, parentElementRect.height);
    this.x = `${ xPos }`;
    this.y = `${ yPos }`;
    this.changeDetector.markForCheck();

    this.transformBoxContentPosition();
  }

  changeFontSize(diff: number): void {
    const newFontSize = this.fontSize + diff;

    if (newFontSize <= FontSize.MINIMUM || newFontSize >= FontSize.MAXIMUM) {
      return;
    }

    this.fontSize = newFontSize;
    this.elementRef.nativeElement.style.fontSize = `${ this.fontSize }${ CSS_SIZE_UNIT_PX }`;
    this.fontSizeChange.emit(this.fontSize);
    this.changeDetector.markForCheck();
  }

  remove(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.removeNote.emit(this.noteId);
    this.resetBoxPosition();
  }

  onWrapperFocus(): void {
    if(this.disabled){
      return;
    }
    if (this.editionMode$subject.getValue() !== EditionMode.NONE) {
      return;
    }
    this.editionMode$subject.next(EditionMode.MOVE_ENABLED);
    this.isTextToolActive.emit(true);
  }

  calculateSelection(event: MouseEvent|TouchEvent): void {
    const target = event.target as HTMLInputElement;
    this.caretIndex = this.utilitiesService.getCaretIndex(target);
  }

  enableWriteMode(event: MouseEvent|TouchEvent): void {
    if(this.disabled){
      return;
    }
    this.changeDetector.detach();
    this.calculateSelection(event);
    if (!this.isConnected) {
      return;
    }
    this.editionMode$subject.next(EditionMode.WRITE_ENABLED);
    this.onHideUnClickedNotes(this.noteId);
    this.changeDetector.detectChanges();
    this.changeDetector.reattach();
  }

  onTextFieldFocus(inputElement: HTMLParagraphElement): void {
    this.supportsTouchDevices &&
    this.isVirtualKeyboardSupported &&
    this.onVirtualKeyboardGeometryChange();
    this.isTextToolActive.emit(true);
    const range = document.createRange()
    const sel = window.getSelection()
    range.setStart(inputElement.childNodes[0], this.caretIndex);
    range.collapse(true);
    sel?.removeAllRanges()
    sel?.addRange(range)
  }

  onTextFieldBlur(): void {
    this.textContentChange.emit(this.inputText);
    if (this.inputText.length) {
      this.editionMode$subject.next(EditionMode.MOVE_ENABLED);
      this.innerWrapper.nativeElement.focus();
    } else {
      this.editionMode$subject.next(EditionMode.NONE);
    }
    if (!this.isConnected) {
      return;
    }
    this.resetBoxPosition();
    setTimeout(() => this.transformBoxContentPosition());
  }

  onClickOutside(): void {
    if (EditionMode.MOVE_ENABLED !== this.editionMode$subject.getValue()) {
      return;
    }
    if (this.isConnected && this.contentIsValid && this.isChanged) {
      this.saving$ubject.next();
      this.resetBoxPosition();
    }
    this.editionMode$subject.next(EditionMode.NONE);
  }

  onHideUnClickedNotes(noteId = ''): void {
    this.hideUnClickedNotes.emit(noteId);
  }

  onVirtualKeyboardGeometryChange(): void {
    if ('virtualKeyboard' in navigator) {
      const virtualKeyboard = (navigator as unknown as { virtualKeyboard: HTMLElement })
        .virtualKeyboard;
      virtualKeyboard.addEventListener('geometrychange', (e: any) =>
        DrawerTextUtil.handleGeometryChange(
          e.target.boundingRect,
          this.elementRef.nativeElement,
          this.isBoxActive,
          this.utilitiesService
        )
      );
    }
  }

  onInputKeypress(event: KeyboardEvent): void {
    this.transformBoxContentPosition();
    !DrawerTextUtil.isAllowed(event, this.maxLengthText) && event.preventDefault();
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.inputText = target.innerText;
    this.isChanged = true;
  }

  resetBoxPosition() {
    this.elementRef.nativeElement.style.top = undefined;
  }

  ngOnDestroy(): void {
    this.isActive.emit(false);
    this.isActiveSubscription.unsubscribe();
    this.subscription.unsubscribe();
    this.isWritingModeSubscription.unsubscribe();
  }
}
